"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Popover = void 0;
const lit_1 = require("lit");
const decorators_js_1 = require("lit/decorators.js");
const overlay_styles_1 = require("@/components/overlay/overlay.styles");
require("@/components/common");
const anchor_controller_1 = require("@/controllers/anchor-controller");
const disclosure_controller_1 = require("@/controllers/disclosure-controller");
const escape_key_controller_1 = require("@/controllers/escape-key-controller");
const outside_click_controller_1 = require("@/controllers/outside-click-controller");
const portal_controller_1 = require("@/controllers/portal-controller");
const schedule_controller_1 = require("@/controllers/schedule-controller");
const utils_1 = require("@/utils");
const open_state_1 = require("@/utils/open-state");
// 열리면 포커스를 먼저 받아 방향키로 탐색하는 목록(없으면 autofocus 요소, 그것도 없으면 표면 자체가 받는다).
// 방향키 탐색은 이 목록이 소유한다.
const LIST_SELECTOR = 'mm-menu-item-group, mm-select-listbox';
/**
 * 트리거에 앵커되어 떠 있는 non-modal 표면. mm-sheet처럼 host가 곧 표면이라 portal 컨테이너로 옮겨지고
 * 내용(자식)이 함께 따라가며, backdrop·스크롤 잠금 없이 배경 상호작용을 막지 않습니다.
 * 트리거는 popover 밖에 두고 aria-controls로 이 popover의 id를 가리킵니다. 클릭 토글·aria-expanded 배선은
 * DisclosureController가, 외부 클릭·ESC 닫기는 popover가 소유합니다. portal로 옮겨져도 트리거를 찾도록
 * 선언한 자리의 root에서 찾습니다. 여는 표면의 종류(aria-haspopup)는 트리거에, role은 안에 넣는 목록 컴포넌트에 둡니다.
 * 트리거를 소비자가 직접 배선하는 쪽(mm-select 등)은 aria-controls 대신 `anchor`로 기준 요소를 넘깁니다.
 * 트리거의 자손이 아니므로 위치는 기준 요소의 화면 좌표를 재어 정하고, 열려 있는 동안 스크롤·리사이즈를 따라갑니다.
 * 열리면 포커스를 표면 안으로 옮기고, Tab으로 표면을 벗어나면 닫고 트리거로 돌려보냅니다.
 * 표면이 문서 끝(portal 컨테이너)에 놓여 Tab으로 나가면 트리거 다음 요소가 아니라 엉뚱한 곳에 닿기 때문입니다.
 * 좌표는 placement prop으로, 폭·여백은 `--overlay-panel-*` 토큰으로 정합니다.
 */
let Popover = class Popover extends (0, open_state_1.withOpenState)(lit_1.LitElement) {
    constructor() {
        super();
        this.placement = 'bottom-start';
        this.portal = new portal_controller_1.PortalController(this);
        this.disclosure = new disclosure_controller_1.DisclosureController(this, {
            getRoot: () => this.portal.originRoot,
        });
        this.outsideClick = new outside_click_controller_1.OutsideClickController(this, () => this.close(), {
            isOpen: () => this.open,
            getSafeElements: () => [this.anchorElement],
        });
        this.escapeKey = new escape_key_controller_1.EscapeKeyController(this);
        this.anchorPosition = new anchor_controller_1.AnchorController(this, {
            getAnchor: () => this.anchorElement,
            isOpen: () => this.open,
            variables: {
                left: '--popover-anchor-left',
                top: '--popover-anchor-top',
                width: '--popover-anchor-width',
                height: '--popover-anchor-height',
            },
        });
        this.focusExitCheck = new schedule_controller_1.ScheduleController(this, () => this.closeIfFocusLeft(), { delay: 0 });
        // portal로 문서 끝에 놓여 Tab으로 밖에 나가면 엉뚱한 곳에 닿으므로, 포커스가 표면을 벗어나면 닫고 트리거로 돌려보낸다.
        // 포커스는 Tab 처리가 끝난 뒤에 옮겨 가므로 한 틱 뒤에 판단한다.
        this.handleKeydown = (event) => {
            if (event.key !== 'Tab')
                return;
            this.focusExitCheck.request();
        };
        // 리스너 대상이 host 자신이라 연결이 바뀌어도 유지되므로 생성자에서 한 번만 등록한다.
        this.addEventListener('keydown', this.handleKeydown);
    }
    render() {
        return (0, lit_1.html) `
      <div class="panel">
        <mm-scroll direction="column">
          <slot></slot>
        </mm-scroll>
      </div>
    `;
    }
    connectedCallback() {
        super.connectedCallback();
        // 열릴 때 목록이 없는 내용은 표면 자체가 포커스를 받을 수 있게 하되, Tab 순서에는 넣지 않는다.
        if (!this.hasAttribute('tabindex'))
            this.tabIndex = -1;
    }
    updated(changedProperties) {
        if (changedProperties.get('open') === undefined)
            return;
        if (this.open) {
            this.focusContent();
            return;
        }
        this.restoreFocus();
    }
    get anchorElement() {
        return this.anchor ?? this.disclosure.trigger;
    }
    closeIfFocusLeft() {
        if (!this.open || this.matches(':focus-within'))
            return;
        this.close();
        this.returnFocus();
    }
    // 열리면 포커스를 표면 안으로 옮기고, 닫힐 때 돌아갈 요소를 기억한다.
    // 목록이 있으면 목록으로, 안의 autofocus 요소가 있으면 그 요소로, 없으면 표면 자체로 옮긴다.
    focusContent() {
        this.returnFocusElement = (0, utils_1.getDeepActiveElement)();
        const target = this.findList() ?? this.querySelector('[autofocus]') ?? this;
        target.focus({ preventScroll: true });
    }
    // 선택 그룹처럼 목록을 자기 shadow 안에 렌더하는 래퍼도 있으므로 한 단계 안까지 찾는다.
    findList() {
        for (const element of this.contentElements) {
            if (element.matches(LIST_SELECTOR))
                return element;
            const list = element.shadowRoot?.querySelector(LIST_SELECTOR);
            if (list)
                return list;
        }
    }
    // 포커스가 표면 안에 있던 채로 닫히면(ESC·항목 선택) 숨겨진 요소에 포커스가 남지 않게 트리거로 돌린다.
    restoreFocus() {
        if (!this.matches(':focus-within'))
            return;
        this.returnFocus();
    }
    returnFocus() {
        ;
        (this.returnFocusElement ?? this.anchorElement)?.focus();
    }
};
Popover.styles = [overlay_styles_1.overlaySurfaceStyles, overlay_styles_1.popoverPositionStyles];
__decorate([
    (0, decorators_js_1.property)({ type: String, reflect: true })
], Popover.prototype, "placement", void 0);
__decorate([
    (0, decorators_js_1.property)({ attribute: false })
], Popover.prototype, "anchor", void 0);
__decorate([
    (0, decorators_js_1.queryAssignedElements)({ flatten: true })
], Popover.prototype, "contentElements", void 0);
Popover = __decorate([
    (0, decorators_js_1.customElement)('mm-popover')
], Popover);
exports.Popover = Popover;
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoicG9wb3Zlci5qcyIsInNvdXJjZVJvb3QiOiIiLCJzb3VyY2VzIjpbInBvcG92ZXIudHMiXSwibmFtZXMiOltdLCJtYXBwaW5ncyI6Ijs7Ozs7Ozs7O0FBQUEsNkJBQXNDO0FBQ3RDLHFEQUFrRjtBQUVsRix3RUFJNEM7QUFDNUMsK0JBQTRCO0FBQzVCLHVFQUFrRTtBQUNsRSwrRUFBMEU7QUFDMUUsK0VBQXlFO0FBQ3pFLHFGQUErRTtBQUMvRSx1RUFBa0U7QUFDbEUsMkVBQXNFO0FBQ3RFLG1DQUE4QztBQUM5QyxtREFBa0Q7QUFPbEQscUVBQXFFO0FBQ3JFLHNCQUFzQjtBQUN0QixNQUFNLGFBQWEsR0FBRyx1Q0FBdUMsQ0FBQTtBQUU3RDs7Ozs7Ozs7Ozs7R0FXRztBQUVJLElBQU0sT0FBTyxHQUFiLE1BQU0sT0FBUSxTQUFRLElBQUEsMEJBQWEsRUFBQyxnQkFBVSxDQUFDO0lBNkJwRDtRQUNFLEtBQUssRUFBRSxDQUFBO1FBNUJrQyxjQUFTLEdBQXFCLGNBQWMsQ0FBQTtRQU0vRSxXQUFNLEdBQUcsSUFBSSxvQ0FBZ0IsQ0FBQyxJQUFJLENBQUMsQ0FBQTtRQUNuQyxlQUFVLEdBQUcsSUFBSSw0Q0FBb0IsQ0FBQyxJQUFJLEVBQUU7WUFDbEQsT0FBTyxFQUFFLEdBQUcsRUFBRSxDQUFDLElBQUksQ0FBQyxNQUFNLENBQUMsVUFBVTtTQUN0QyxDQUFDLENBQUE7UUFDTSxpQkFBWSxHQUFHLElBQUksaURBQXNCLENBQUMsSUFBSSxFQUFFLEdBQUcsRUFBRSxDQUFDLElBQUksQ0FBQyxLQUFLLEVBQUUsRUFBRTtZQUMxRSxNQUFNLEVBQUUsR0FBRyxFQUFFLENBQUMsSUFBSSxDQUFDLElBQUk7WUFDdkIsZUFBZSxFQUFFLEdBQUcsRUFBRSxDQUFDLENBQUMsSUFBSSxDQUFDLGFBQWEsQ0FBQztTQUM1QyxDQUFDLENBQUE7UUFDTSxjQUFTLEdBQUcsSUFBSSwyQ0FBbUIsQ0FBQyxJQUFJLENBQUMsQ0FBQTtRQUN6QyxtQkFBYyxHQUFHLElBQUksb0NBQWdCLENBQUMsSUFBSSxFQUFFO1lBQ2xELFNBQVMsRUFBRSxHQUFHLEVBQUUsQ0FBQyxJQUFJLENBQUMsYUFBYTtZQUNuQyxNQUFNLEVBQUUsR0FBRyxFQUFFLENBQUMsSUFBSSxDQUFDLElBQUk7WUFDdkIsU0FBUyxFQUFFO2dCQUNULElBQUksRUFBRSx1QkFBdUI7Z0JBQzdCLEdBQUcsRUFBRSxzQkFBc0I7Z0JBQzNCLEtBQUssRUFBRSx3QkFBd0I7Z0JBQy9CLE1BQU0sRUFBRSx5QkFBeUI7YUFDbEM7U0FDRixDQUFDLENBQUE7UUFDTSxtQkFBYyxHQUFHLElBQUksd0NBQWtCLENBQUMsSUFBSSxFQUFFLEdBQUcsRUFBRSxDQUFDLElBQUksQ0FBQyxnQkFBZ0IsRUFBRSxFQUFFLEVBQUUsS0FBSyxFQUFFLENBQUMsRUFBRSxDQUFDLENBQUE7UUF1Q2xHLDBFQUEwRTtRQUMxRSx5Q0FBeUM7UUFDakMsa0JBQWEsR0FBRyxDQUFDLEtBQW9CLEVBQUUsRUFBRTtZQUMvQyxJQUFJLEtBQUssQ0FBQyxHQUFHLEtBQUssS0FBSztnQkFBRSxPQUFNO1lBRS9CLElBQUksQ0FBQyxjQUFjLENBQUMsT0FBTyxFQUFFLENBQUE7UUFDL0IsQ0FBQyxDQUFBO1FBekNDLG9EQUFvRDtRQUNwRCxJQUFJLENBQUMsZ0JBQWdCLENBQUMsU0FBUyxFQUFFLElBQUksQ0FBQyxhQUFhLENBQUMsQ0FBQTtJQUN0RCxDQUFDO0lBRUQsTUFBTTtRQUNKLE9BQU8sSUFBQSxVQUFJLEVBQUE7Ozs7OztLQU1WLENBQUE7SUFDSCxDQUFDO0lBRUQsaUJBQWlCO1FBQ2YsS0FBSyxDQUFDLGlCQUFpQixFQUFFLENBQUE7UUFDekIsMkRBQTJEO1FBQzNELElBQUksQ0FBQyxJQUFJLENBQUMsWUFBWSxDQUFDLFVBQVUsQ0FBQztZQUFFLElBQUksQ0FBQyxRQUFRLEdBQUcsQ0FBQyxDQUFDLENBQUE7SUFDeEQsQ0FBQztJQUVTLE9BQU8sQ0FBQyxpQkFBdUM7UUFDdkQsSUFBSSxpQkFBaUIsQ0FBQyxHQUFHLENBQUMsTUFBTSxDQUFDLEtBQUssU0FBUztZQUFFLE9BQU07UUFFdkQsSUFBSSxJQUFJLENBQUMsSUFBSSxFQUFFO1lBQ2IsSUFBSSxDQUFDLFlBQVksRUFBRSxDQUFBO1lBQ25CLE9BQU07U0FDUDtRQUVELElBQUksQ0FBQyxZQUFZLEVBQUUsQ0FBQTtJQUNyQixDQUFDO0lBRUQsSUFBWSxhQUFhO1FBQ3ZCLE9BQU8sSUFBSSxDQUFDLE1BQU0sSUFBSSxJQUFJLENBQUMsVUFBVSxDQUFDLE9BQU8sQ0FBQTtJQUMvQyxDQUFDO0lBVU8sZ0JBQWdCO1FBQ3RCLElBQUksQ0FBQyxJQUFJLENBQUMsSUFBSSxJQUFJLElBQUksQ0FBQyxPQUFPLENBQUMsZUFBZSxDQUFDO1lBQUUsT0FBTTtRQUV2RCxJQUFJLENBQUMsS0FBSyxFQUFFLENBQUE7UUFDWixJQUFJLENBQUMsV0FBVyxFQUFFLENBQUE7SUFDcEIsQ0FBQztJQUVELDBDQUEwQztJQUMxQyw0REFBNEQ7SUFDcEQsWUFBWTtRQUNsQixJQUFJLENBQUMsa0JBQWtCLEdBQUcsSUFBQSw0QkFBb0IsR0FBNkIsQ0FBQTtRQUMzRSxNQUFNLE1BQU0sR0FBRyxJQUFJLENBQUMsUUFBUSxFQUFFLElBQUksSUFBSSxDQUFDLGFBQWEsQ0FBYyxhQUFhLENBQUMsSUFBSSxJQUFJLENBQUE7UUFDeEYsTUFBTSxDQUFDLEtBQUssQ0FBQyxFQUFFLGFBQWEsRUFBRSxJQUFJLEVBQUUsQ0FBQyxDQUFBO0lBQ3ZDLENBQUM7SUFFRCx1REFBdUQ7SUFDL0MsUUFBUTtRQUNkLEtBQUssTUFBTSxPQUFPLElBQUksSUFBSSxDQUFDLGVBQWUsRUFBRTtZQUMxQyxJQUFJLE9BQU8sQ0FBQyxPQUFPLENBQUMsYUFBYSxDQUFDO2dCQUFFLE9BQU8sT0FBTyxDQUFBO1lBQ2xELE1BQU0sSUFBSSxHQUFHLE9BQU8sQ0FBQyxVQUFVLEVBQUUsYUFBYSxDQUFjLGFBQWEsQ0FBQyxDQUFBO1lBQzFFLElBQUksSUFBSTtnQkFBRSxPQUFPLElBQUksQ0FBQTtTQUN0QjtJQUNILENBQUM7SUFFRCwrREFBK0Q7SUFDdkQsWUFBWTtRQUNsQixJQUFJLENBQUMsSUFBSSxDQUFDLE9BQU8sQ0FBQyxlQUFlLENBQUM7WUFBRSxPQUFNO1FBRTFDLElBQUksQ0FBQyxXQUFXLEVBQUUsQ0FBQTtJQUNwQixDQUFDO0lBRU8sV0FBVztRQUNqQixDQUFDO1FBQUEsQ0FBQyxJQUFJLENBQUMsa0JBQWtCLElBQUksSUFBSSxDQUFDLGFBQWEsQ0FBQyxFQUFFLEtBQUssRUFBRSxDQUFBO0lBQzNELENBQUM7O0FBMUdNLGNBQU0sR0FBRyxDQUFDLHFDQUFvQixFQUFFLHNDQUFxQixDQUFDLENBQUE7QUFDbEI7SUFBMUMsSUFBQSx3QkFBUSxFQUFDLEVBQUUsSUFBSSxFQUFFLE1BQU0sRUFBRSxPQUFPLEVBQUUsSUFBSSxFQUFFLENBQUM7MENBQTZDO0FBRXZEO0lBQS9CLElBQUEsd0JBQVEsRUFBQyxFQUFFLFNBQVMsRUFBRSxLQUFLLEVBQUUsQ0FBQzt1Q0FBcUI7QUFFcEQ7SUFEQyxJQUFBLHFDQUFxQixFQUFDLEVBQUUsT0FBTyxFQUFFLElBQUksRUFBRSxDQUFDO2dEQUNGO0FBTjVCLE9BQU87SUFEbkIsSUFBQSw2QkFBYSxFQUFDLFlBQVksQ0FBQztHQUNmLE9BQU8sQ0E0R25CO0FBNUdZLDBCQUFPIn0=